/**
 * CalendarModal Component
 * Reusable modal that wraps DashboardCalendar for use anywhere in the app
 *
 * Usage:
 * - From Leads page: <CalendarModal open={show} onClose={close} lead={lead} />
 * - From Clients page: <CalendarModal open={show} onClose={close} client={client} />
 * - From Quotes page: <CalendarModal open={show} onClose={close} lead={lead} quote={quote} />
 * - Date selection mode: <CalendarModal open={show} onClose={close} onDateSelect={handleDate} />
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, UserIcon, EnvelopeIcon, PhoneIcon, DocumentTextIcon, CheckIcon } from '@heroicons/react/24/outline';
import { DashboardCalendar } from '@/modules/dashboard/components/DashboardCalendar';

// Context types for pre-filling events
export interface CalendarLeadContext {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

export interface CalendarClientContext {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

export interface CalendarQuoteContext {
  id: string;
  number: string;
  unitName?: string;
}

interface CalendarModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Lead context - pre-fills event with lead info */
  lead?: CalendarLeadContext;
  /** Client context - pre-fills event with client info */
  client?: CalendarClientContext;
  /** Quote context - adds quote reference to event */
  quote?: CalendarQuoteContext;
  /** Optional title override */
  title?: string;
  /** If provided, enables date selection mode with a "Seleccionar" footer */
  onDateSelect?: (date: Date) => void;
  /** Minimum selectable date (YYYY-MM-DD) */
  minDate?: string;
  /** Maximum selectable date (YYYY-MM-DD) */
  maxDate?: string;
  /** Initial date to show in the calendar */
  initialDate?: Date;
}

export const CalendarModal: React.FC<CalendarModalProps> = ({
  open,
  onClose,
  lead,
  client,
  quote,
  title,
  onDateSelect,
  minDate,
  maxDate,
  initialDate,
}) => {
  const [pickedDate, setPickedDate] = useState<Date | null>(initialDate || null);

  if (!open) return null;

  // Determine the context name for display
  const contextName = lead?.name || client?.name;
  const contextType = lead ? 'Lead' : client ? 'Cliente' : null;
  const displayTitle = title || (contextName ? `Agendar con ${contextName}` : 'Calendario');

  const isDateInRange = (date: Date): boolean => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    if (minDate && dateStr < minDate) return false;
    if (maxDate && dateStr > maxDate) return false;
    return true;
  };

  const handleSelectDate = () => {
    if (pickedDate && onDateSelect && isDateInRange(pickedDate)) {
      onDateSelect(pickedDate);
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 2147483646 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
        style={{ zIndex: 2147483647 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {displayTitle}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Context Banner - Lead/Client Info */}
        {(lead || client) && (
          <div className="flex-shrink-0 px-4 py-3 bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 border-b border-sky-200 dark:border-sky-800">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-sky-100 dark:bg-sky-900/50 rounded-full flex items-center justify-center">
                <UserIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white truncate">
                    {contextName}
                  </span>
                  <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 rounded-full">
                    {contextType}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {(lead?.email || client?.email) && (
                    <span className="flex items-center gap-1 truncate">
                      <EnvelopeIcon className="h-3 w-3 flex-shrink-0" />
                      {lead?.email || client?.email}
                    </span>
                  )}
                  {(lead?.phone || client?.phone) && (
                    <span className="flex items-center gap-1">
                      <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                      {lead?.phone || client?.phone}
                    </span>
                  )}
                </div>
              </div>
              {quote && (
                <div className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                  <div className="text-xs">
                    <div className="font-medium text-gray-900 dark:text-white">Cotización {quote.number}</div>
                    {quote.unitName && (
                      <div className="text-gray-500 dark:text-gray-400">{quote.unitName}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar */}
        <div className="flex-1 overflow-hidden">
          <DashboardCalendar
            leadContext={lead}
            clientContext={client}
            quoteContext={quote}
            initialDate={initialDate}
            onDateChange={onDateSelect ? (date) => setPickedDate(date) : undefined}
            minDate={minDate}
            maxDate={maxDate}
            readOnly={!!onDateSelect}
          />
        </div>

        {/* Date Selection Footer */}
        {onDateSelect && (
          <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {pickedDate
                ? <span className="capitalize">{pickedDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                : 'Selecciona un día en el calendario'}
            </span>
            <div className="flex items-center gap-3">
              {pickedDate && (() => {
                const todayMs = new Date(new Date().toISOString().split('T')[0] + 'T00:00:00').getTime();
                const selectedMs = new Date(pickedDate.getFullYear(), pickedDate.getMonth(), pickedDate.getDate()).getTime();
                const daysUntil = Math.round((selectedMs - todayMs) / 86400000);
                if (daysUntil <= 0) return null;
                return (
                  <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                    en {daysUntil} día{daysUntil !== 1 ? 's' : ''}
                  </span>
                );
              })()}
              <button
                onClick={handleSelectDate}
                disabled={!pickedDate || !isDateInRange(pickedDate)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-gray-800 text-white hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <CheckIcon className="h-4 w-4" />
                Seleccionar esta fecha
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CalendarModal;
